export const GSR_ADC_MIN = 0;
export const GSR_ADC_MAX = 1023;

export type GsrSignal = {
  rawAdc: number | null;
  smoothAdc: number | null;
  uiLevel: number | null;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function scaleGsrUiLevel(adcValue: unknown): number | null {
  const numeric = toFiniteNumber(adcValue);
  if (numeric == null) return null;

  const clamped = clamp(numeric, GSR_ADC_MIN, GSR_ADC_MAX);
  return Number(((clamped / GSR_ADC_MAX) * 100).toFixed(1));
}

export function createGsrSignal(values: {
  rawAdc?: unknown;
  smoothAdc?: unknown;
}): GsrSignal {
  const rawAdc = toFiniteNumber(values.rawAdc);
  const smoothAdc = toFiniteNumber(values.smoothAdc) ?? rawAdc;
  const resolvedRawAdc = rawAdc ?? smoothAdc;

  return {
    rawAdc: resolvedRawAdc,
    smoothAdc,
    uiLevel: scaleGsrUiLevel(smoothAdc),
  };
}

export type BaselineLike = {
  baseline_gsr?: unknown;
  baseline_gsr_raw_adc?: unknown;
  baseline_gsr_smooth_adc?: unknown;
  baseline_gsr_ui_level?: unknown;
} | null | undefined;

export function readBaselineRawAdc(baseline: BaselineLike): number | null {
  return toFiniteNumber(baseline?.baseline_gsr_raw_adc);
}

export function readBaselineSmoothAdc(baseline: BaselineLike): number | null {
  return (
    toFiniteNumber(baseline?.baseline_gsr_smooth_adc) ??
    toFiniteNumber(baseline?.baseline_gsr)
  );
}

export function readBaselineUiLevel(baseline: BaselineLike): number | null {
  return (
    toFiniteNumber(baseline?.baseline_gsr_ui_level) ??
    scaleGsrUiLevel(readBaselineSmoothAdc(baseline))
  );
}
